import { Box, Button, Card } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import {
  BooleanField,
  Datagrid,
  FunctionField,
  List,
  RefreshButton,
  SelectArrayInput,
  SelectInput,
  TextField,
  TopToolbar,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import FilterGroup from '../../../components/FilterGroup';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { paths } from '../../../../../routes/paths';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import XTextInput from '../../../components/XTextInput';
import { Resources } from '../../resource';
import { bbVerifySocialFollow, BbSocialFollowType } from '../../../services/blindBoxService';
import { SECTION_CARD_SX } from '../shared/sectionCardSx';

const FOLLOW_TYPE_CHOICES = (Object.values(BbSocialFollowType) as BbSocialFollowType[]).map(
  (id) => ({
    id,
    name: id,
  })
);

const FOLLOWING_FILTER_CHOICES = [
  { id: 'true', name: 'module.blindBox.socialFollowFollowingYes' },
  { id: 'false', name: 'module.blindBox.socialFollowFollowingNo' },
];

function VerifySocialFollowCell({ record }: { record: Record<string, unknown> }) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  const userId = String(record?.userId ?? '').trim();
  const rowType = String(record?.socialType ?? '').trim();

  const onVerify = async () => {
    if (!userId) return;
    if (!rowType) {
      notify(t('module.blindBox.socialFollowMissingTypeRow'), { type: 'warning' });
      return;
    }
    setLoading(true);
    try {
      await bbVerifySocialFollow({ userId, type: rowType });
      notify(t('module.blindBox.socialFollowVerifyOk'), { type: 'success' });
      refresh();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PermissionButton resource="bb-social-followers" action="update">
      <Button size="small" variant="outlined" disabled={loading} onClick={onVerify}>
        {t('module.blindBox.socialFollowVerify')}
      </Button>
    </PermissionButton>
  );
}

export default function BlindBoxSocialFollowersList() {
  const t = useTranslate();

  const filters = useMemo(
    () => [
      <FilterGroup key="social-follow-filters" alwaysOn spacing={2} p={{ xs: 0, sm: 1 }} pt={2}>
        <SelectArrayInput
          key="types"
          source="types"
          label={t('module.blindBox.socialFollowType')}
          choices={FOLLOW_TYPE_CHOICES}
          alwaysOn
          sx={{ marginBottom: 0, minWidth: 220 }}
        />
        <SelectInput
          key="isFollowing"
          source="isFollowing"
          label={t('module.blindBox.socialFollowingFilter')}
          emptyText={t('actions.all')}
          choices={FOLLOWING_FILTER_CHOICES}
          alwaysOn
          sx={{ marginBottom: 0, minWidth: 160 }}
        />
        <XTextInput key="userId" source="userId" label="userId" alwaysOn />
        <XTextInput
          key="walletAddress"
          source="walletAddress"
          label={t('module.blindBox.socialWalletAddress')}
          alwaysOn
        />
        <XTextInput
          key="socialUsername"
          source="socialUsername"
          label={t('module.blindBox.socialUsername')}
          alwaysOn
        />
      </FilterGroup>,
    ],
    [t]
  );

  const actions = (
    <TopToolbar>
      <RefreshButton />
    </TopToolbar>
  );

  return (
    <PermissionGuard resource="bb-social-followers" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.blindBox.socialFollowers')}
          links={[
            { name: t('module.blindBox.title'), href: paths.blindBox.overview.root },
            { name: t('module.blindBox.socialFollowers') },
          ]}
        />

        <Card variant="outlined" sx={{ ...SECTION_CARD_SX, mt: 2 }}>
          <Box sx={{ px: 2, pt: 2, pb: 0, typography: 'body2', color: 'text.secondary' }}>
            {t('module.blindBox.socialFollowVerifyHint')}
          </Box>
          <List
            resource={Resources.blindBoxSocialFollowers}
            exporter={false}
            perPage={50}
            actions={actions}
            filters={filters as any}
            sort={{ field: 'boundAt', order: 'DESC' }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid bulkActionButtons={false} rowClick={false}>
                <TextField source="userId" label="userId" />
                <TextField source="socialType" label={t('module.blindBox.socialFollowType')} />
                <TextField
                  source="socialTypeLabel"
                  label={t('module.blindBox.socialFollowTypeLabel')}
                />
                <TextField source="socialUsername" label={t('module.blindBox.socialUsername')} />
                <BooleanField
                  source="isFollowing"
                  label={t('module.blindBox.socialFollowingFilter')}
                />
                <FunctionField
                  label={t('module.blindBox.followCheckedAt')}
                  render={(r: any) =>
                    r?.followCheckedAt
                      ? dayjs(r.followCheckedAt).format('YYYY-MM-DD HH:mm:ss')
                      : '—'
                  }
                />
                <FunctionField
                  label={t('module.blindBox.boundAt')}
                  render={(r: any) =>
                    r?.boundAt ? dayjs(r.boundAt).format('YYYY-MM-DD HH:mm:ss') : '—'
                  }
                />
                <FunctionField
                  label={t('module.blindBox.socialFollowActions')}
                  render={(r: any) => (r?.userId ? <VerifySocialFollowCell record={r} /> : null)}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
}
